import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Thermometer, 
  Zap, 
  Flame,
  ChevronDown, 
  CheckCircle, 
  ArrowRight,
  Phone
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img from "../Images/sol.jpg";
import img1 from "../Images/st.jpg";
import img2 from "../Images/sp.jpg"
import img3 from "../Images/sheat.png";
import img4 from "../Images/under.jpeg";
import img5 from "../Images/economic.jpg";
import img6 from "../Images/future.webp";

const Solar = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const services = {
    solarThermal: {
      title: "Solar Thermal Systems",
      icon: <Thermometer className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Advanced solar thermal solutions that harness the sun's energy for efficient water heating and industrial process heat applications.",
      features: [
        "High-efficiency solar collectors",
        "Advanced heat transfer systems",
        "Thermal storage integration",
        "Smart temperature control",
        "Scalable system design",
        "Low maintenance requirements"
      ],
      applications: [
        "Industrial process heating",
        "Commercial water heating",
        "Space heating systems",
        "Swimming pool heating",
        "District heating networks",
        "Agricultural processing"
      ],
      benefits: [
        "Reduced energy costs",
        "Lower carbon footprint",
        "Consistent heat supply",
        "Long system lifespan",
        "Minimal operating costs"
      ],
      image: img1
    },
    solarPV: {
      title: "Solar Photovoltaic Systems",
      icon: <Zap className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "State-of-the-art photovoltaic systems that convert sunlight directly into electricity for various industrial and commercial applications.",
      features: [
        "High-efficiency PV panels",
        "Advanced inverter technology",
        "Smart monitoring systems",
        "Modular design approach",
        "Grid integration capability",
        "Battery storage options"
      ],
      applications: [
        "Industrial power generation",
        "Commercial buildings",
        "Manufacturing facilities",
        "Warehouses",
        "Agricultural operations",
        "Remote power systems"
      ],
      benefits: [
        "Energy independence",
        "Reduced electricity costs",
        "Peak load reduction",
        "Low maintenance",
        "Environmental compliance"
      ],
      image: img2
    },
    solarHeating: {
      title: "Solar Based Heating",
      icon: <Flame className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Innovative solar heating solutions that combine multiple technologies for optimal heating efficiency in industrial and commercial settings.",
      features: [
        "Hybrid system integration",
        "Advanced heat distribution",
        "Intelligent control systems",
        "Thermal energy storage",
        "Weather-adaptive operation",
        "Backup heating integration"
      ],
      applications: [
        "Industrial process heating",
        "Space heating",
        "Greenhouse heating",
        "Drying applications",
        "Hot water systems",
        "Hybrid heating solutions"
      ],
      benefits: [
        "Year-round operation",
        "Reduced fossil fuel dependency",
        "Stable heating costs",
        "System flexibility",
        "Enhanced efficiency"
      ],
      image: img3
    }
  };

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Solar Energy Solutions",
    "description": "Comprehensive solar energy systems including thermal, photovoltaic, and heating solutions for industrial and commercial applications",
    "itemListElement": Object.entries(services).map(([key, service], index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": service.title,
      "description": service.description,
      "image": service.image,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Gortherm Energy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Solar Energy Solutions | Thermal, PV & Heating Systems | Gortherm Energy</title>
        <meta name="description" content="Comprehensive solar energy solutions including solar thermal systems, photovoltaic panels, and solar heating for industrial and commercial applications. Sustainable renewable energy systems for reduced costs and environmental impact." />
        <meta name="keywords" content="solar energy, solar thermal systems, solar photovoltaic, solar PV panels, solar heating, renewable energy, solar power, industrial solar solutions, commercial solar systems, solar water heating, solar electricity, green energy Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Solar Energy Solutions | Thermal, PV & Heating Systems | Gortherm Energy" />
        <meta property="og:description" content="Harnessing solar power for sustainable and energy-efficient future. Complete solar thermal, photovoltaic, and heating solutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/solar-energy" />
        <meta property="og:image" content={img} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Solar Energy Solutions | Gortherm Energy" />
        <meta name="twitter:description" content="Advanced solar thermal, photovoltaic, and heating systems for industrial and commercial applications." />
        <meta name="twitter:image" content={img} />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/solar-energy" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section */}
        <motion.header
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-[300px] sm:h-[400px] lg:h-[500px] overflow-hidden"
        >
          <img
            src={img}
            alt="Solar energy solutions - renewable power systems by Gortherm Energy"
            className="w-full h-full object-cover"
            loading="eager"
            width="1920"
            height="500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="max-w-2xl"
              >
                <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight mb-4 sm:mb-6 leading-tight">
                  Solar Energy
                  <span className="text-yellow-400"> Solutions</span>
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-8">
                  Harnessing solar power for a sustainable and energy-efficient future
                </p>
              </motion.div>
            </div>
          </div>
        </motion.header>

        {/* Main Services Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 sm:mb-12">
            Our Solar Solutions
          </h2>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
          >
            {Object.entries(services).map(([key, service], idx) => (
              <motion.article
                key={key}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                onHoverStart={() => setHoveredCard(idx)}
                onHoverEnd={() => setHoveredCard(null)}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
                itemScope
                itemType="https://schema.org/Product"
              >
                <div className="relative h-40 sm:h-48">
                  <img
                    src={service.image}
                    alt={`${service.title} - Advanced solar energy technology`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="300"
                    itemProp="image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="text-yellow-600" aria-hidden="true">
                      {service.icon}
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900" itemProp="name">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-4" itemProp="description">
                    {service.description}
                  </p>
                  <button
                    onClick={() => setExpandedSection(expandedSection === idx ? null : idx)}
                    className="flex items-center space-x-2 text-yellow-600 hover:text-yellow-700 transition-colors text-sm sm:text-base font-medium"
                    aria-expanded={expandedSection === idx}
                    aria-controls={`solar-details-${idx}`}
                  >
                    <span>Learn more</span>
                    <motion.div
                      animate={{ rotate: expandedSection === idx ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {expandedSection === idx && (
                      <motion.div
                        id={`solar-details-${idx}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 space-y-4 sm:space-y-6"
                      >
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Key Features</h4>
                          <ul className="grid grid-cols-1 gap-2">
                            {service.features.map((feature, i) => (
                              <li key={i} className="flex items-start space-x-2">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span className="text-xs sm:text-sm text-gray-600">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Applications</h4>
                          <ul className="grid grid-cols-1 gap-2">
                            {service.applications.map((app, i) => (
                              <li key={i} className="flex items-start space-x-2">
                                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span className="text-xs sm:text-sm text-gray-600">{app}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Benefits</h4>
                          <ul className="grid grid-cols-1 gap-2">
                            {service.benefits.map((benefit, i) => (
                              <li key={i} className="flex items-start space-x-2">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span className="text-xs sm:text-sm text-gray-600">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </section>

        {/* Educational Content Section */}
        <section className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-8 sm:mb-12"
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ 
                  duration: 0.5,
                  type: "spring",
                  stiffness: 100
                }}
                className="relative inline-block mb-4"
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="absolute -bottom-2 left-0 h-1 bg-yellow-500 rounded-full"
                />
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 relative z-10">
                  Solar Energy Insights
                </h2>
              </motion.div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <motion.article
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl overflow-hidden shadow-lg"
              >
                <div className="p-4 sm:p-6">
                  <img
                    src={img4}
                    alt="Understanding solar energy efficiency and performance metrics"
                    className="w-full h-40 sm:h-48 object-cover mb-4 rounded-lg"
                    loading="lazy"
                    width="400"
                    height="200"
                  />
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
                    Understanding Solar Energy Efficiency
                  </h3>
                  <div className="prose prose-yellow">
                    <p className="text-sm sm:text-base text-gray-600 mb-4 text-justify">
                      Solar energy efficiency has made remarkable strides in recent years, with modern solar cells achieving conversion rates of up to 23% in commercial applications. This improvement in efficiency, coupled with decreased manufacturing costs, has made solar energy increasingly competitive with traditional power sources.
                    </p>
                    <p className="text-sm sm:text-base text-gray-600 text-justify">
                      Key factors affecting solar efficiency include:
                    </p>
                    <ul className="text-sm sm:text-base text-gray-600 mb-4 list-disc pl-4 text-justify">
                      <li>Panel orientation and tilt angle</li>
                      <li>Temperature and weather conditions</li>
                      <li>Shading and obstruction factors</li>
                      <li>Maintenance and cleaning practices</li>
                    </ul>
                  </div>
                </div>
              </motion.article>

              <motion.article
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl overflow-hidden shadow-lg"
              >
                <div className="p-4 sm:p-6">
                  <img
                    src={img5}
                    alt="Economic benefits and ROI of solar energy investments"
                    className="w-full h-40 sm:h-48 object-cover mb-4 rounded-lg"
                    loading="lazy"
                    width="400"
                    height="200"
                  />
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 text-justify">
                    The Economics of Solar Investment
                  </h3>
                  <div className="prose prose-yellow">
                    <p className="text-sm sm:text-base text-gray-600 mb-4 text-justify">
                      The financial benefits of solar energy systems extend far beyond immediate energy savings. Modern solar installations typically offer:
                    </p>
                    <ul className="text-sm sm:text-base text-gray-600 mb-4 list-disc pl-4 text-justify">
                      <li>ROI periods of 4-6 years on average</li>
                      <li>25+ years of operational lifespan</li>
                      <li>Significant tax incentives and rebates</li>
                      <li>Protection against rising energy costs</li>
                    </ul>
                    <p className="text-sm sm:text-base text-gray-600 text-justify">
                      With current technology and incentives, businesses can expect to save 40-70% on their energy costs over the system's lifetime.
                    </p>
                  </div>
                </div>
              </motion.article>

              <motion.article
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl overflow-hidden shadow-lg"
              >
                <div className="p-4 sm:p-6">
                  <img
                    src={img6}
                    alt="Future innovations in solar energy technology"
                    className="w-full h-40 sm:h-48 object-cover mb-4 rounded-lg"
                    loading="lazy"
                    width="400"
                    height="200"
                  />
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 text-justify">
                    Future of Solar Technology
                  </h3>
                  <div className="prose prose-yellow">
                    <p className="text-sm sm:text-base text-gray-600 mb-4 text-justify">
                      The solar industry continues to evolve with emerging technologies and innovations:
                    </p>
                    <ul className="text-sm sm:text-base text-gray-600 mb-4 list-disc pl-4 text-justify">
                      <li>Bifacial solar panels with dual-sided power generation</li>
                      <li>Perovskite solar cells with improved efficiency</li>
                      <li>Integrated storage solutions with smart grid capabilities</li>
                      <li>Building-integrated photovoltaics (BIPV)</li>
                    </ul>
                    <p className="text-sm sm:text-base text-gray-600 text-justify">
                      These advancements are expected to further increase efficiency while reducing costs, making solar energy an increasingly attractive option for businesses and industries.
                    </p>
                  </div>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <aside className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ready to Harness Solar Power?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our solar energy experts are ready to help you design and implement 
              the perfect renewable energy solution for your facility.
            </p>
            <a 
              href="tel:+9779802781400"
              className="inline-block"
              aria-label="Contact solar energy experts at +977 980 278 1400"
            >
              <button className="bg-yellow-600 hover:bg-yellow-700 active:bg-yellow-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                <span>Contact Our Experts</span>
              </button>
            </a>
            
          </div>
        </aside>
      </main>
    </>
  );
};

export default Solar;